import {
  GATE_COMORBID_IDS,
  selectedComorbidityIds,
} from "../SharedProfile/showIf";

export const wantsFullWidth = (question: any): boolean => {
  const type = question?.type;
  if (question?.multi === true) return true;
  if (
    type === "recall" ||
    type === "subjective" ||
    type === "chips" ||
    type === "cards" ||
    type === "emoji"
  ) {
    return true;
  }
  if (
    (type === "stepper" || type === "slider") &&
    Number(question.max ?? 10) - Number(question.min ?? 0) > 11
  ) {
    return true;
  }
  return false;
};

/** Full-width when the control needs it, or when a half-width field would sit alone. */
export const computeSpanFullIds = (questions: any[]): Set<string> => {
  const wide = questions.map(wantsFullWidth);
  const full = new Set<string>();
  let col = 0;
  for (let i = 0; i < questions.length; i++) {
    const id = String(questions[i]?.id ?? i);
    if (wide[i]) {
      full.add(id);
      col = 0;
      continue;
    }
    const nextIsWideOrEnd = i + 1 >= questions.length || wide[i + 1];
    if (col === 0 && nextIsWideOrEnd) {
      full.add(id);
      col = 0;
    } else {
      col = col === 0 ? 1 : 0;
    }
  }
  return full;
};

export type QuestionBlock =
  | { kind: "loose"; questions: any[] }
  | { kind: "group"; key: string; label: string; questions: any[] };

export const clusterComorbidityQuestions = (
  questions: any[],
  answers: Record<string, unknown>,
): QuestionBlock[] => {
  const gate = questions.find((q) => GATE_COMORBID_IDS.includes(String(q.id)));
  const rest = questions.filter((q) => !GATE_COMORBID_IDS.includes(String(q.id)));
  const followUps = rest.filter(
    (q) =>
      Array.isArray(q.includesComorbidities) && q.includesComorbidities.length,
  );
  const other = rest.filter(
    (q) =>
      !Array.isArray(q.includesComorbidities) ||
      !q.includesComorbidities.length,
  );

  if (!followUps.length) {
    return questions.length ? [{ kind: "loose", questions }] : [];
  }

  const labelById: Record<string, string> = {};
  for (const option of gate?.options || []) {
    if (option?.id != null) {
      labelById[String(option.id)] = option.label || String(option.id);
    }
  }

  const selected = selectedComorbidityIds(answers);
  const buckets = new Map<string, any[]>();
  for (const question of followUps) {
    const matched = (question.includesComorbidities as string[])
      .map(String)
      .filter((id) => selected.includes(id));
    const key = (matched.length ? matched : question.includesComorbidities)
      .map(String)
      .sort()
      .join("|");
    const list = buckets.get(key) || [];
    list.push(question);
    buckets.set(key, list);
  }

  const orderedKeys = Array.from(buckets.keys()).sort((a, b) => {
    const rank = (key: string) => {
      const idxs = key
        .split("|")
        .map((id) => selected.indexOf(id))
        .filter((n) => n >= 0);
      return idxs.length ? Math.min(...idxs) : 999;
    };
    return rank(a) - rank(b);
  });

  const blocks: QuestionBlock[] = [];
  if (gate) blocks.push({ kind: "loose", questions: [gate] });
  if (other.length) blocks.push({ kind: "loose", questions: other });
  for (const key of orderedKeys) {
    const ids = key.split("|");
    const label =
      buckets.get(key)?.[0]?.groupLabel ||
      ids.map((id: string) => labelById[id] || id).join(" / ");
    blocks.push({
      kind: "group",
      key,
      label,
      questions: buckets.get(key) || [],
    });
  }
  return blocks;
};
